import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProvisionResultRow } from 'core/hooks';

import * as S from './PandaDocProvisioning.styled';

interface IProvisioningResultProps {
  results: ProvisionResultRow[] | null;
  topLevelErrors: string | null;
  /** pandadocId → display name, so each result line names a person. */
  docNameById: Record<string, string>;
}

/**
 * Turns the backend `error` / `errors` JSON strings into a readable phrase.
 * Values are either a plain string or `{ message, code }` (tz-backend.md §6.3).
 */
export const humanizeProvisionError = (raw?: string | null): string => {
  if (!raw) {
    return '';
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      return parsed;
    }
    const messages = Object.values(parsed as Record<string, unknown>).map(
      (value) => {
        if (value && typeof value === 'object' && 'message' in value) {
          return String((value as { message: unknown }).message);
        }
        return String(value);
      },
    );
    return messages.filter(Boolean).join('; ');
  } catch {
    return raw;
  }
};

/** Result panel shown after a provisioning run — one line per document. */
export const ProvisioningResult = memo(
  ({ results, topLevelErrors, docNameById }: IProvisioningResultProps) => {
    const { t } = useTranslation();

    if (topLevelErrors) {
      return (
        <S.ResultPanel>
          <S.ResultTitle>
            {t('pages.pandadocProvisioning.result.failTitle')}
          </S.ResultTitle>
          <S.ResultItem variant="error">
            <S.Badge variant="error">
              {t('pages.pandadocProvisioning.result.badgeError')}
            </S.Badge>
            <span>{humanizeProvisionError(topLevelErrors)}</span>
          </S.ResultItem>
        </S.ResultPanel>
      );
    }

    if (!results || !results.length) {
      return null;
    }

    return (
      <S.ResultPanel>
        <S.ResultTitle>
          {t('pages.pandadocProvisioning.result.title')}
        </S.ResultTitle>
        {results.map((result) => {
          const name =
            docNameById[result.pandadocId ?? ''] ||
            result.pandadocId ||
            t('pages.pandadocProvisioning.result.defaultDocName');

          if (result.success) {
            return (
              <S.ResultItem key={result.pandadocId} variant="ok">
                <S.Badge variant="ok">
                  {t('pages.pandadocProvisioning.result.badgeSuccess')}
                </S.Badge>
                <span>
                  {name}
                  {t('pages.pandadocProvisioning.result.successSuffix')}
                </span>
              </S.ResultItem>
            );
          }

          if (result.skipped) {
            return (
              <S.ResultItem key={result.pandadocId} variant="neutral">
                <S.Badge variant="neutral">
                  {t('pages.pandadocProvisioning.result.badgeSkipped')}
                </S.Badge>
                <span>
                  {name}
                  {t('pages.pandadocProvisioning.result.skippedSuffix')}
                </span>
              </S.ResultItem>
            );
          }

          return (
            <S.ResultItem key={result.pandadocId} variant="error">
              <S.Badge variant="error">
                {t('pages.pandadocProvisioning.result.badgeError')}
              </S.Badge>
              <span>
                {name}
                {humanizeProvisionError(result.error)
                  ? t('pages.pandadocProvisioning.result.errorSuffix', {
                      error: humanizeProvisionError(result.error),
                    })
                  : ''}
              </span>
            </S.ResultItem>
          );
        })}
      </S.ResultPanel>
    );
  },
);
